import { useCallback, useRef, useState } from 'react';
import type { FabricImage } from 'fabric';
import { useImageOperations } from '@/hooks/useImageOperations';
import { useDesignStore } from '@/stores/designStore';
import { AssetUploadError, uploadAssetForCanvas } from '@/api/assets';
import {
  ALLOWED_IMAGE_TYPES,
  DIRECT_SALE_UPLOAD_ACCEPT_LIST,
  MAX_FILE_SIZE,
} from '@/config/constants';
import { useIsPhone } from './useIsPhone';

export type DirectSaleUploadError =
  | 'format'
  | 'size'
  | 'upload'
  /** An EPS: the server cannot read it, the customer can save it as PDF. */
  | 'eps'
  /** A PDF or AI the server could not open or render (multi-page, broken). */
  | 'unreadable';

export const DIRECT_SALE_UPLOAD_ACCEPT =
  DIRECT_SALE_UPLOAD_ACCEPT_LIST.join(',');

/**
 * Longest edge of the bitmap a phone edits with. The original goes up
 * unchanged and is what gets printed; a 12-megapixel photo held in full,
 * plus the canvas that trims it, is what makes Safari on iOS drop the tab.
 */
export const PHONE_PREVIEW_MAX_EDGE = 2000;

type UploadKind = 'image' | 'pdf' | 'eps' | 'unknown';

/**
 * What a file is, by the type the browser reports and, failing that, by
 * its extension — an iOS picker hands over AI and EPS files with an empty
 * or generic type.
 */
export function classifyUpload(file: {
  name: string;
  type: string;
}): UploadKind {
  const type = file.type.toLowerCase();
  const ext = file.name.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1] ?? '';
  if ((ALLOWED_IMAGE_TYPES as readonly string[]).includes(type)) return 'image';
  if (type === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (ext === 'ai' || type === 'application/illustrator') return 'pdf';
  if (ext === 'eps' || type === 'application/postscript') return 'eps';
  return 'unknown';
}
export const DIRECT_SALE_UPLOAD_MAX_MB = Math.round(
  MAX_FILE_SIZE / 1024 / 1024
);

/**
 * A customer's file onto the product, with outcomes they can see.
 *
 * Validates against ALLOWED_IMAGE_TYPES/MAX_FILE_SIZE itself — the image
 * hook's inline `image/*` check accepts anything image-shaped and only logs
 * to the console, invisible to an end customer. The list is the
 * browser-decodable one: the file is placed on the canvas before it is
 * uploaded, so a PDF would not survive the local step. A failed upload
 * removes the placed image again — an image the print pipeline cannot fetch
 * must not survive into checkout.
 *
 * A PDF (or a PDF-compatible AI file) takes the other road: no browser
 * decodes it, so it goes to the server first, which renders the page to
 * PNG, and that PNG is placed. EPS the server cannot read; the customer is
 * told to save it as PDF.
 *
 * Shared by the desktop's upload panel and the phone, where the upload tile
 * opens the system's own picker (photo library, camera, files) directly.
 */
export function useDirectSaleUpload({ onDone }: { onDone?: () => void } = {}) {
  const placedRef = useRef<FabricImage | null>(null);
  // One upload at a time: a second file while the first is in flight would
  // take over placedRef, and a later failure of the first would then remove
  // the wrong image.
  const busyRef = useRef(false);
  const { addImageFromFile, addImageFromUrl } = useImageOperations();
  const isPhone = useIsPhone();
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<DirectSaleUploadError | null>(null);

  const handleFile = useCallback(
    async (file: File) => {
      if (busyRef.current) return;
      const kind = classifyUpload(file);
      if (kind === 'eps') {
        setError('eps');
        return;
      }
      if (kind === 'unknown') {
        setError('format');
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        setError('size');
        return;
      }

      setError(null);
      setIsUploading(true);
      busyRef.current = true;
      const done = () => {
        busyRef.current = false;
        setIsUploading(false);
      };

      if (kind === 'pdf') {
        // Declared as PDF whatever the picker said: the server checks the
        // bytes and answers 400 for anything it cannot render.
        const asPdf = new File([file], file.name.replace(/\.ai$/i, '.pdf'), {
          type: 'application/pdf',
        });
        try {
          const asset = await uploadAssetForCanvas(asPdf);
          const img = await addImageFromUrl(asset.url, asset.assetId, {
            placement: 'print-area-fitted',
          });
          done();
          if (!img) {
            setError('upload');
            return;
          }
          onDone?.();
        } catch (e) {
          done();
          setError(
            e instanceof AssetUploadError && e.status === 400
              ? 'unreadable'
              : 'upload'
          );
        }
        return;
      }

      const img = await addImageFromFile(
        file,
        {
          onUploadComplete: () => {
            placedRef.current = null;
            done();
            onDone?.();
          },
          onUploadFailed: () => {
            // Cross-store canvas access — a cleanup failure must not break the
            // caller (cf. docs/solutions/ui-bugs/wrong-product-mockup-edit-placement.md).
            try {
              const canvas = useDesignStore.getState().canvas;
              if (canvas && placedRef.current) {
                canvas.remove(placedRef.current);
                canvas.discardActiveObject();
                canvas.requestRenderAll();
              }
            } catch {
              // The failed object stays; the customer can delete it manually.
            }
            placedRef.current = null;
            done();
            setError('upload');
          },
        },
        {
          // Fitted into the print area, high on it — a chest print, not a
          // file dropped onto the middle of the mockup.
          placement: 'print-area-fitted',
          previewMaxEdge: isPhone ? PHONE_PREVIEW_MAX_EDGE : undefined,
        }
      );

      if (!img) {
        done();
        setError('upload');
        return;
      }
      placedRef.current = img;
    },
    [addImageFromFile, addImageFromUrl, isPhone, onDone]
  );

  const clearError = useCallback(() => setError(null), []);

  return { handleFile, isUploading, error, clearError };
}
